import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

interface ActionDialogProps {
  heading?: string;
  message?: string;
  confirmButtonMessage?: string;
  isConfirming?: boolean;
  isDisabled?: boolean;
  cancelButtonMessage?: string;
  confirm?: () => void;
  cancel?: () => void;
  children?: ReactNode;
  dataTestId?: string;
  confirmButtonDataTestId?: string;
  cancelButtonDataTestId?: string;
}

export function ActionDialog({
  heading,
  message,
  confirmButtonMessage,
  isConfirming,
  cancelButtonMessage,
  confirm,
  cancel,
  isDisabled,
  children,
  dataTestId,
  confirmButtonDataTestId,
  cancelButtonDataTestId,
}: ActionDialogProps) {
  const handleClose = () => {
    if (isConfirming) {
      return;
    }
    if (cancel) {
      cancel();
    }
  };

  const derive = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : undefined;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!!isConfirming}
      slotProps={{ paper: { "data-testid": derive("dialog") } }}
    >
      <Stack direction="column" spacing={5}>
        {heading && (
          <Typography
            variant="body1DashedDivider"
            data-testid={derive("heading-text")}
          >
            {heading}
          </Typography>
        )}

        {message && (
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ whiteSpace: "pre-line" }}
            data-testid={derive("message-text")}
          >
            {message}
          </Typography>
        )}
        {children}

        {(confirm || cancel) && (
          <Stack spacing={1}>
            {confirm && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={confirm}
                disabled={isDisabled}
                loading={isConfirming}
                data-testid={
                  confirmButtonDataTestId ?? derive("confirm-button")
                }
              >
                {confirmButtonMessage}
              </Button>
            )}

            {cancel && (
              <Button
                variant="text"
                size="large"
                fullWidth
                onClick={cancel}
                loading={false}
                data-testid={cancelButtonDataTestId ?? derive("cancel-button")}
              >
                {cancelButtonMessage}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
